import { useState } from 'react';
import { useDispatch } from 'react-redux';

import AddDataSourcePage from 'dashboard/forms/FormNewDataSource/AddDataSourcePage';
import SelectS3ObjectPage from 'dashboard/forms/FormNewDataSource/SelectS3ObjectPage';
import {
  AddDataSourceError,
  AddDataSourceResponse,
  FormNewDataSourceData,
  FormNewDataSourceDataS3,
  NewDataSourceType,
} from 'dashboard/forms/FormNewDataSource/types';
import Form from 'dashboard/layouts/FormLayout/Form';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  GoogleSheetsFilesResponse,
  S3Config,
  S3Object,
} from 'dashboard/models/external-integration';
import { showMessage } from 'dashboard/redux/actions/messages';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely as useForm } from 'lib/security/reactHookForm';

interface FormNewDataSourceProps {
  onSubmit: (data: FormNewDataSourceData) => Promise<AddDataSourceResponse>;
  s3Config: S3Config | null;
  handleListS3Objects: (values: FormNewDataSourceDataS3) => Promise<S3Object[]>;
  getAllGoogleSheets: (code: string, redirectUrl: string) => Promise<GoogleSheetsFilesResponse>;
  dataSourceType?: NewDataSourceType | null;
}

export const FormNewDataSource = ({
  onSubmit,
  s3Config,
  handleListS3Objects,
  getAllGoogleSheets,
  dataSourceType = null,
}: FormNewDataSourceProps) => {
  const dispatch = useDispatch();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [s3Objects, setS3Objects] = useState<S3Object[]>(null);

  const form = useForm<FormNewDataSourceData>({
    defaultValues: {
      port: '3306',
      // default credential type for s3
      credentials: {
        type: 'cross_account',
      },
      type: dataSourceType || undefined,
    },
    mode: 'onChange',
  });

  const { handleSubmit, getValues, setError, clearErrors, watch, resetField } = form;

  const onS3Connect = async () => {
    const values = getValues();
    if (values.type === ApiModel.ExternalIntegrationDataSourceS3) {
      try {
        const objects = await handleListS3Objects(values);
        // validate that the s3 objects look reasonable.
        // If 1000 objects are returned, it's likely we have reached the page limit, in which we will ask them
        // to be more specific with the path of their csv
        const LIST_S3_OBJECTS_PAGE_SIZE = 1000;
        if (objects.length >= LIST_S3_OBJECTS_PAGE_SIZE) {
          setError('csvPathPrefix', {
            type: 'custom',
            message:
              'Too many objects found within this path prefix. Try narrowing the path further.',
          });
          return;
        }

        // We only support csv objects. Error if the objects return do not include any csvs
        const selectableS3Objects = objects.filter((object) => {
          // should end with '.csv'
          // should only contain '/' and safe characters listed here:
          // https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html
          return /^[a-zA-Z0-9_\-/() .*!']+\.csv$/.test(object.key);
        });

        if (selectableS3Objects.length === 0) {
          setError('csvPathPrefix', {
            type: 'custom',
            message:
              'No CSVs were found with this path prefix. Are you sure the bucket and path are correct?',
          });
          return;
        }

        // form values good so far. clear any current errors
        clearErrors();
        setS3Objects(selectableS3Objects.sort((objA, objB) => (objA.key < objB.key ? -1 : 1)));
      } catch (error) {
        if (error instanceof ApiNetworkError) {
          dispatch(
            showMessage(
              'We are experiencing network issues. Please reload to try again.',
              MessageType.Failure,
              false,
            ),
          );
          return;
        }
        if (error instanceof ApiResponseError && error.status === 422) {
          // clear current errors to replace with new ones
          clearErrors();

          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          const errorDetails = (error?.errors[0]?.details || '').toString();
          // validation error
          if (errorDetails.includes('validation error detected')) {
            setError('credentials.arn', { type: 'custom', message: 'Invalid ARN' });
          } else if (errorDetails.includes('is not authorized to perform')) {
            setError('credentials', {
              type: 'custom',
              message: 'Unable to assumeRole: Access Denied',
            });
          } else if (errorDetails.includes('bucket does not exist')) {
            setError('bucket', {
              type: 'custom',
              message: `Unable to find bucket ${values.bucket} in region ${values.region}`,
            });
          } else {
            setError('bucket', {
              type: 'custom',
              message: `Unable to list objects from bucket`,
            });
          }
        }
      }
    }
  };

  const switchToDataSourcePage = () => {
    resetField('objectKeys');
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setS3Objects(null);
  };

  const parseOnSubmitError = (err: AddDataSourceError) => {
    switch (watch('type')) {
      // most likely a problem with the CSV itself, assign it to that
      case ApiModel.ExternalIntegrationDataSourceCsv: {
        setError('file', {
          type: 'custom',
          message: err,
        });
        break;
      }
      // until we have other errors show up, imma shove it as a name error
      default: {
        setError('name', {
          type: 'custom',
          message: err,
        });
      }
    }
  };

  const handleOnSubmit = async (form: FormNewDataSourceData) => {
    const response = await onSubmit(form);
    if (response && typeof response === 'string') {
      parseOnSubmitError(response);
    }
  };

  return (
    <Form>
      {watch('type') === ApiModel.ExternalIntegrationDataSourceS3 && s3Objects && s3Config ? (
        <SelectS3ObjectPage
          form={form}
          s3Config={s3Config}
          s3Objects={s3Objects}
          onBack={switchToDataSourcePage}
          onSubmit={handleSubmit(handleOnSubmit)}
        />
      ) : (
        <AddDataSourcePage
          form={form}
          s3Config={s3Config}
          onS3Connect={onS3Connect}
          getAllGoogleSheets={getAllGoogleSheets}
          dataSourceType={dataSourceType}
          onSubmit={handleSubmit(handleOnSubmit)}
        />
      )}
    </Form>
  );
};
